import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { PageHeader } from "@/components/data-display/page-header";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { EmptyState } from "@/components/ui/feedback";
import { Table, TableWrapper } from "@/components/ui/table";
import { EmptyRow, Panel } from "@/features/oversight/oversight-ui";
import { WipHint } from "@/components/ui/wip-hint";
import { PolicyRuleMappingPreview } from "@/pages/enterprise/policy-rule-mapping-preview";

/**
 * WIP layout port (round 3, ALL-5825) of the designer's `/governance/:id`
 * screen — `.design-ref/prototype/app/(enterprise-wallet)/governance/[id]/page.tsx`
 * → `PolicyDetail` in `features/oversight/governance.tsx`.
 *
 * The layout is the designer's (breadcrumb, page header, the 320px attribute
 * column beside the condition/wallet/evaluation panels); the data is not, and
 * never was: policy definitions have no backend route in this environment yet
 * (context/page_pipeline/BACKEND_GAPS.md), so every value renders the shared
 * "not available" placeholder and every table its empty state.
 *
 * Deliberately no API call, no fixture import and no invented record — this
 * route previously published authored fixtures (invented policies, conditions
 * and approvers), which is why it was deferred. The `<WipHint />` chip beside
 * the title is what a person sees instead of a banner, and it comes off in the
 * PR that wires the real endpoint.
 */
export default function GovernancePolicyPage() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Governance", href: "/governance" }, { label: "Policy" }]} />

      <PageHeader
        title="Policy"
        subtitle="Policy definitions evaluated against every transfer intent before signing."
        actions={<WipHint />}
      />

      <p className="mb-5 text-xs text-muted">
        Policy definitions have no backend route in this environment yet — the sections below are the designed layout with
        no policy data.
      </p>

      <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div>
          <Panel
            title={
              <span className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" aria-hidden />
                Policy Attributes
              </span>
            }
            bodyClassName="px-5 py-3"
          >
            <dl className="divide-y divide-line">
              {["Policy ID", "Type", "Status", "Created", "Last Modified"].map((label) => (
                <div key={label} className="flex items-center justify-between gap-3 py-2.5 text-xs">
                  <dt className="shrink-0 text-muted">{label}</dt>
                  <dd className="min-w-0 text-right font-semibold text-ink">
                    <UnavailableValue />
                  </dd>
                </div>
              ))}
            </dl>
          </Panel>

          <Panel title="Approval Route" bodyClassName="px-5 py-4">
            <EmptyState
              className="py-8"
              title="No approval route to show"
              description="Approver assignments come from the policy definition, which has no backend route in this environment yet."
            />
          </Panel>
        </div>

        <div>
          <Panel title="Conditions" bodyClassName="px-5 py-4">
            <EmptyState
              className="py-8"
              title="No conditions to show"
              description="Condition rows come from the policy definition, which has no backend route in this environment yet."
            />
          </Panel>

          <Panel title="Affected Wallets" bodyClassName="p-0">
            <TableWrapper className="rounded-none border-0">
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                    <th className="px-4 py-2.5">Wallet</th>
                    <th className="px-4 py-2.5">Network</th>
                    <th className="px-4 py-2.5">Type</th>
                  </tr>
                </thead>
                <tbody>
                  <EmptyRow colSpan={3}>No wallet bindings to show.</EmptyRow>
                </tbody>
              </Table>
            </TableWrapper>
          </Panel>

          {/* A bare text link is ~16px tall; the lane's tap-target floor is
              36px, so the hit area is padded without changing the visual
              weight of the label. */}
          <Panel
            title="Recent Evaluations"
            actions={
              <Link
                to="/governance/evaluation"
                className="inline-flex min-h-[36px] items-center text-xs font-semibold text-primary hover:underline"
              >
                View all
              </Link>
            }
            bodyClassName="p-0"
          >
            <TableWrapper className="rounded-none border-0">
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                    <th className="px-4 py-2.5">Eval ID</th>
                    <th className="px-4 py-2.5">Intent</th>
                    <th className="px-4 py-2.5">Result</th>
                    <th className="px-4 py-2.5">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  <EmptyRow colSpan={4}>No evaluations recorded for this policy yet.</EmptyRow>
                </tbody>
              </Table>
            </TableWrapper>
          </Panel>
        </div>
      </div>

      <PolicyRuleMappingPreview
        state={{ status: "unavailable", reasonCode: "POLICY_RULE_MAPPING_UNAVAILABLE" }}
      />
    </div>
  );
}
